import { useCallback, useEffect, useState } from "react";
import { Link } from "wouter";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  ResponsiveDialog,
  ResponsiveDialogBody,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogFooterHint,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from "@/components/ui/ResponsiveDialog";
import { TarjetasOpcion } from "@/components/ds/TarjetasOpcion";
import { Icon } from "@/components/ds/Icon";
import { CampoFecha } from "@/components/ds/CampoFecha";
import { Check, Loader2, Save } from "@/components/ds/icons";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * Ventanas de aviso que el usuario puede elegir. Espeja
 * `App\Support\VentanasAviso::OPCIONES` en fc-api — si acá aparece una que allá
 * no está en la lista, el backend responde 422 al guardar.
 */
const VENTANAS = [60, 30, 15, 7] as const;

/** Atajos de fecha, en meses. */
const PRESETS: ReadonlyArray<{ label: string; meses: number }> = [
  { label: "+6 meses", meses: 6 },
  { label: "+1 año", meses: 12 },
  { label: "+2 años", meses: 24 },
  { label: "+3 años", meses: 36 },
];

type TipoVigencia = "fecha" | "permanente";

/** Fecha local `YYYY-MM-DD`. A propósito NO usa `toISOString()`: en Chile (UTC-3/-4)
 *  devuelve el día siguiente después de las 21:00. */
function aIso(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${dd}`;
}

function hoyIso(): string {
  return aIso(new Date());
}

/** Parseo manual: `new Date("2026-07-21")` es medianoche UTC — un día atrás en Chile. */
function desdeIso(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** Suma meses sin overflow: 31-ene + 1 mes = 28/29-feb, no 3-mar. */
function sumarMeses(iso: string, meses: number): string {
  const base = desdeIso(iso);
  const dia = base.getDate();
  base.setDate(1);
  base.setMonth(base.getMonth() + meses);
  const ultimoDia = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
  base.setDate(Math.min(dia, ultimoDia));
  return aIso(base);
}

function diasHasta(iso: string): number {
  const ms = desdeIso(iso).getTime() - desdeIso(hoyIso()).getTime();
  return Math.round(ms / 86_400_000);
}

function restarDias(iso: string, dias: number): string {
  const d = desdeIso(iso);
  d.setDate(d.getDate() - dias);
  return aIso(d);
}

/**
 * Antepone la preposición "a" contrayéndola con el artículo: "el trabajador"
 * → "al trabajador". Concatenar a secas escribe "a el trabajador", que es la
 * clase de error que ningún typecheck ve.
 */
function conPreposicionA(frase: string): string {
  return frase.startsWith("el ") ? `al ${frase.slice(3)}` : `a ${frase}`;
}

function fechaCorta(iso: string): string {
  return desdeIso(iso).toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** Documento del que se edita la vigencia. Las tres familias
 *  (empresa / persona / recurso) comparten exactamente esta forma. */
export interface VigenciaDocumento {
  fecha_vencimiento: string | null;
  notificar_dias: number[];
}

export interface VigenciaModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Nombre del documento — subtítulo del encabezado. */
  label: string;
  documento: VigenciaDocumento | null;
  onSubmit: (opts: { fechaVencimiento: string | null; notificarDias: number[] }) => Promise<void>;
  /**
   * Correo que recibe una copia del aviso además del de la empresa, y cómo se
   * nombra a su dueño en el texto. Hoy sólo lo pasan los documentos de
   * trabajador: el aviso le llega en copia oculta porque el documento es suyo.
   *
   * La regla la manda fc-api (`EnviarDocumentoPorVencerMail`); acá sólo se
   * describe. Si la empresa no tiene correo de vencimientos, este correo pasa a
   * ser el destinatario principal — el aviso NO se pierde.
   */
  copia?: { email: string; etiqueta: string } | null;
}

/**
 * Modal de vigencia de un documento — componente ÚNICO del Design System.
 *
 * Reemplaza las tres copias de `SetVencimientoModal` (empresa, persona,
 * recurso) que eran clones deliberados: el mismo formulario mantenido en tres
 * archivos divergía en cuanto alguien tocaba uno solo.
 *
 * Sólo edita `fecha_vencimiento` + `notificar_dias`; la subida del archivo vive
 * en la celda de acciones de cada tabla.
 *
 * El aviso del día del vencimiento NO se configura: sale siempre para cualquier
 * documento con fecha (ver `App\Support\VentanasAviso::DIA_VENCIMIENTO`). Por eso
 * aparece en la previsualización como una fila fija marcada "siempre" y no como
 * un chip elegible — sería mentira ofrecer apagarlo.
 */
export function VigenciaModal({
  open,
  onOpenChange,
  label,
  documento,
  onSubmit,
  copia = null,
}: VigenciaModalProps) {
  const [tipo, setTipo] = useState<TipoVigencia>("permanente");
  const [fecha, setFecha] = useState("");
  const [ventanas, setVentanas] = useState<number[]>([]);
  const [guardando, setGuardando] = useState(false);

  // Correo de destino. Se consulta al abrir porque no viaja en el contexto de
  // usuario, y sirve para avisar cuando NO está configurado: en ese caso el
  // job de vencimientos loguea y omite el envío, así que prometer avisos sin
  // decirlo sería engañar.
  const [emailEmpresa, setEmailEmpresa] = useState<string | null>(null);
  const [emailCargado, setEmailCargado] = useState(false);

  useEffect(() => {
    if (!open) return;
    setGuardando(false);
    if (documento?.fecha_vencimiento) {
      setTipo("fecha");
      setFecha(documento.fecha_vencimiento);
      setVentanas(documento.notificar_dias ?? []);
    } else {
      setTipo("permanente");
      setFecha("");
      setVentanas([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, documento?.fecha_vencimiento]);

  useEffect(() => {
    if (!open || emailCargado) return;
    let vivo = true;
    api
      .get<{ data: { email_para_vencimientos: string | null } }>("/me/empresa")
      .then((res) => vivo && setEmailEmpresa(res.data.email_para_vencimientos || null))
      .catch(() => vivo && setEmailEmpresa(null))
      .finally(() => vivo && setEmailCargado(true));
    return () => {
      vivo = false;
    };
  }, [open, emailCargado]);

  const elegirTipo = useCallback((next: TipoVigencia) => {
    setTipo(next);
    if (next === "fecha") {
      setFecha((prev) => prev || sumarMeses(hoyIso(), 12));
      setVentanas((prev) => (prev.length ? prev : [30, 7]));
    }
  }, []);

  function alternarVentana(dias: number) {
    setVentanas((prev) =>
      prev.includes(dias) ? prev.filter((d) => d !== dias) : [...prev, dias],
    );
  }

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    if (tipo === "fecha") {
      if (!fecha) {
        toast.error("Elige la fecha de vencimiento, o marca «No vence».");
        return;
      }
      if (fecha < hoyIso()) {
        toast.error("La fecha de vencimiento no puede ser anterior a hoy.");
        return;
      }
    }

    setGuardando(true);
    try {
      await onSubmit({
        fechaVencimiento: tipo === "fecha" ? fecha : null,
        notificarDias: tipo === "fecha" ? [...ventanas].sort((a, b) => a - b) : [],
      });
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo guardar la vigencia.");
    } finally {
      setGuardando(false);
    }
  }

  const dias = tipo === "fecha" && fecha ? diasHasta(fecha) : null;
  const ordenadas = [...ventanas].sort((a, b) => b - a);

  // Sólo se advierte "no se enviarán" cuando NO queda ningún destinatario:
  // sin correo de empresa, la copia (el trabajador) recibe el aviso igual.
  const emailCopia = copia?.email?.trim() ? copia : null;
  const hayDestinatario = Boolean(emailEmpresa) || emailCopia !== null;

  return (
    <ResponsiveDialog open={open} onOpenChange={(next) => !guardando && onOpenChange(next)}>
      <ResponsiveDialogContent>
        <form onSubmit={guardar} className="flex min-h-0 flex-1 flex-col">
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle>Vigencia del documento</ResponsiveDialogTitle>
            <ResponsiveDialogDescription className="fc-truncate">
              {label}
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <ResponsiveDialogBody>
            <section>
              <Etiqueta>Tipo de vigencia</Etiqueta>
              <TarjetasOpcion
                columnas={2}
                valor={tipo}
                onCambiar={(v) => elegirTipo(v as TipoVigencia)}
                disabled={guardando}
                opciones={[
                  {
                    valor: "fecha",
                    titulo: "Vence en una fecha",
                    icono: "event",
                  },
                  {
                    valor: "permanente",
                    titulo: "No vence",
                    icono: "all_inclusive",
                  },
                ]}
              />
            </section>

            {tipo === "permanente" ? (
              <div className="bg-surface-muted flex items-start gap-2.5 rounded-[9px] border p-3">
                <Icon name="all_inclusive" size={17} className="text-icon-soft mt-px shrink-0" />
                <p className="text-[11.5px] leading-[1.5] text-muted-foreground">
                  Este documento no caduca: no aparecerá en «Por vencer» ni generará
                  recordatorios. Se puede reemplazar cuando la empresa lo actualice.
                </p>
              </div>
            ) : (
              <>
                <section>
                  <Etiqueta>Fecha de vencimiento</Etiqueta>
                  <div className="flex items-center gap-2">
                    {/* `CampoFecha` y no `<input type="date">`: el nativo dibuja
                        el valor según el idioma del NAVEGADOR —el mismo 3 de
                        julio se lee `07/03/2026` en un Chrome en inglés— y para
                        una fecha de vencimiento eso es inaceptable.

                        Además el `min` del nativo sólo grisa el calendario: se
                        podía TIPEAR una fecha pasada igual, y el bloqueo recién
                        aparecía como toast al guardar. Acá `min` deshabilita los
                        días anteriores (`disabled={{ before }}`) y no hay tipeo
                        libre, así que una fecha vencida deja de ser elegible. */}
                    <CampoFecha
                      value={fecha}
                      min={hoyIso()}
                      disabled={guardando}
                      onChange={setFecha}
                      aria-label="Fecha de vencimiento"
                      className="flex-1"
                    />
                    {dias !== null && (
                      <span
                        className={cn(
                          "inline-flex h-[26px] shrink-0 items-center rounded-md px-2.5 text-[11.5px] font-medium",
                          dias < 0
                            ? "bg-destructive-bg text-destructive"
                            : dias <= 60
                              ? "bg-warning-bg text-warning"
                              : "bg-success-bg text-success",
                        )}
                      >
                        {dias < 0
                          ? `venció hace ${Math.abs(dias)} días`
                          : dias === 0
                            ? "vence hoy"
                            : `en ${dias} días`}
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {PRESETS.map(({ label: texto, meses }) => {
                      const candidata = sumarMeses(hoyIso(), meses);
                      const activo = fecha === candidata;
                      return (
                        <Chip
                          key={meses}
                          activo={activo}
                          disabled={guardando}
                          onClick={() => setFecha(candidata)}
                        >
                          {texto}
                        </Chip>
                      );
                    })}
                  </div>
                </section>

                <section>
                  <div className="mb-[7px] flex items-baseline justify-between gap-2">
                    <Etiqueta className="mb-0">Recordatorios antes de vencer</Etiqueta>
                    <span className="text-icon-soft text-[11px]">
                      {ventanas.length === 0
                        ? "sólo el día del vencimiento"
                        : `${ventanas.length} ${ventanas.length === 1 ? "aviso" : "avisos"} + el día del vencimiento`}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {VENTANAS.map((d) => {
                      const activo = ventanas.includes(d);
                      return (
                        <Chip
                          key={d}
                          activo={activo}
                          disabled={guardando}
                          onClick={() => alternarVentana(d)}
                          aria-pressed={activo}
                        >
                          {activo && <Check className="h-3.5 w-3.5" />}
                          {d} días antes
                        </Chip>
                      );
                    })}
                  </div>
                </section>

                {fecha && (
                  <section className="overflow-hidden rounded-[9px] border">
                    <div className="border-border-subtle bg-surface-muted text-meta border-b px-[11px] py-2 text-[11px] font-semibold tracking-[0.04em] uppercase">
                      Cuándo saldrá cada aviso
                    </div>
                    {ordenadas.map((d) => {
                      const cuando = restarDias(fecha, d);
                      const pasado = diasHasta(cuando) < 0;
                      return (
                        <FilaAviso
                          key={d}
                          icono={
                            pasado ? (
                              <Icon name="history" size={16} className="text-icon-soft shrink-0" />
                            ) : (
                              <Icon name="mail" size={16} className="text-primary shrink-0" />
                            )
                          }
                          fecha={fechaCorta(cuando)}
                          fechaClase={pasado ? "text-icon-soft" : "text-foreground"}
                          texto={`${d} días antes del vencimiento`}
                          nota={pasado ? "ya pasó" : undefined}
                        />
                      );
                    })}
                    {/* Fila fija: el aviso del día del vencimiento no se puede
                        apagar, por eso va marcada "siempre" y no como un chip. */}
                    <FilaAviso
                      icono={
                        <Icon name="priority_high" size={16} className="text-destructive shrink-0" />
                      }
                      fecha={fechaCorta(fecha)}
                      fechaClase="text-destructive"
                      texto="El día del vencimiento"
                      nota="siempre"
                      ultima
                    />
                  </section>
                )}

                <div
                  className={cn(
                    "flex items-start gap-2.5 rounded-[9px] border p-3",
                    hayDestinatario ? "bg-surface-muted" : "border-warning/30 bg-warning-bg",
                  )}
                >
                  <Icon
                    name={hayDestinatario ? "mail" : "warning"}
                    size={16}
                    className={cn(
                      "mt-px shrink-0",
                      hayDestinatario ? "text-icon-soft" : "text-warning",
                    )}
                  />
                  <p className="flex-1 text-[11.5px] leading-[1.5] text-muted-foreground">
                    {!emailCargado ? (
                      "Cargando el correo de destino…"
                    ) : emailEmpresa ? (
                      <>
                        Los avisos llegan a <strong className="text-foreground">{emailEmpresa}</strong>,
                        el correo de la empresa
                        {emailCopia ? (
                          <>
                            , con copia {conPreposicionA(emailCopia.etiqueta)} (
                            <strong className="text-foreground">{emailCopia.email}</strong>)
                          </>
                        ) : null}
                        .
                      </>
                    ) : emailCopia ? (
                      <>
                        Tu empresa no tiene un correo para vencimientos, así que estos avisos llegan
                        solo {conPreposicionA(emailCopia.etiqueta)} (
                        <strong className="text-foreground">{emailCopia.email}</strong>).
                      </>
                    ) : (
                      <>
                        Tu empresa no tiene un correo para vencimientos, así que{" "}
                        <strong className="text-foreground">estos avisos no se enviarán</strong>.
                      </>
                    )}
                  </p>
                  {emailCargado && (
                    <Link
                      href="/configuracion/empresa"
                      className="text-primary-hover hover:text-primary-strong shrink-0 text-[11.5px] font-medium"
                    >
                      {emailEmpresa ? "Cambiar" : "Configurar"}
                    </Link>
                  )}
                </div>
              </>
            )}
          </ResponsiveDialogBody>

          <ResponsiveDialogFooter>
            <ResponsiveDialogFooterHint>
              {tipo === "permanente"
                ? "No genera alertas ni entra en «Por vencer»"
                : emailEmpresa
                  ? emailCopia
                    ? `Los avisos llegan por correo a ${emailEmpresa} y ${conPreposicionA(emailCopia.etiqueta)}`
                    : `Los avisos llegan por correo a ${emailEmpresa}`
                  : emailCopia
                    ? `Los avisos llegan por correo ${conPreposicionA(emailCopia.etiqueta)}`
                    : "Sin correo de vencimientos configurado"}
            </ResponsiveDialogFooterHint>
            <Button
              type="button"
              variant="outline"
              disabled={guardando}
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={guardando} className="gap-2">
              {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Guardar vigencia
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}

function Etiqueta({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "text-meta mb-[7px] text-[11px] font-semibold tracking-[0.04em] uppercase",
        className,
      )}
    >
      {children}
    </div>
  );
}

function Chip({
  activo,
  children,
  ...props
}: React.ComponentProps<"button"> & { activo: boolean }) {
  return (
    <button
      type="button"
      className={cn(
        "inline-flex h-[31px] items-center gap-1.5 rounded-full border px-3 text-[12.5px] font-medium transition-colors disabled:pointer-events-none disabled:opacity-50",
        activo
          ? "border-primary bg-primary-100 text-primary-strong"
          : "border-input text-muted-foreground hover:border-primary-border",
      )}
      {...props}
    >
      {children}
    </button>
  );
}

function FilaAviso({
  icono,
  fecha,
  fechaClase,
  texto,
  nota,
  ultima = false,
}: {
  /** Nodo, no nombre de glifo: ver la nota del call site sobre el verificador
   *  del subset de íconos. */
  icono: React.ReactNode;
  fecha: string;
  fechaClase: string;
  texto: string;
  nota?: string;
  ultima?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-2.5 px-[11px] py-2",
        !ultima && "border-border-faint border-b",
      )}
    >
      {icono}
      <span className={cn("w-[92px] shrink-0 font-mono text-xs", fechaClase)}>{fecha}</span>
      <span className="flex-1 text-[11.5px] text-muted-foreground">{texto}</span>
      {nota && <span className="text-icon-soft shrink-0 text-[10.5px]">{nota}</span>}
    </div>
  );
}
